import React, { forwardRef, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Slide,
    Select,
    MenuItem,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IconCircleCheck } from "@tabler/icons-react";
import PhoneSelector from "../../../../../components/Controls/PhoneSelector/PhoneSelector";
import { useSelector } from "react-redux";
import { AppStore } from "../../../../../redux/store";
import Button from "../../../../../components/form/Button";
import MaskInput from "../../../../../components/Controls/MaskInput";
import FormControlError from "../../../../../components/Controls/FormControlError";
import { useDocuments } from "../../../../GuestOffer/hooks/useDocuments";
import useCallApiAndLoad from "../../../../../hooks/useCallApiAndLoad";

import { createDeveloperCustomer, createPreBooking } from "../../../services/devsServices";

const Transition = forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export interface CreateBookingFormData {
    username: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    document_type: string;
    document_number: string;
}

const schema = yup.object({
    first_name: yup.string().required("El nombre es requerido"),
    last_name: yup.string().required("El apellido es requerido"),
    email: yup
        .string()
        .email("Ingresa un correo electrónico válido")
        .required("El correo electrónico es requerido"),
    phone: yup.string().required("El teléfono es requerido"),
    username: yup.string(),
    document_type: yup.string().required("Selecciona un tipo de documento"),
    document_number: yup.string().required("El número de documento es requerido"),
}).required();

interface CreateBookingModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    unitId: string;
    unitName?: string;
    token: string;
}

const CreateBookingModal: React.FC<CreateBookingModalProps> = ({
    open,
    onClose,
    onSuccess,
    unitId,
    unitName,
    token,
}) => {
    const { country } = useSelector((state: AppStore) => state.country);
    const { getDataDocuments, loading, documents } = useDocuments();
    const { callEndpoint } = useCallApiAndLoad();

    const [documentMask, setDocumentMask] = useState<string>("");
    const [dialCode, setDialCode] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);

    // Estado para el diálogo de confirmación cuando el usuario ya existe
    const [showExistingConfirm, setShowExistingConfirm] = useState(false);
    const [pendingLid, setPendingLid] = useState("");

    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<CreateBookingFormData>({
        resolver: yupResolver(schema),
        defaultValues: {
            username: "",
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            document_type: "",
            document_number: "",
        },
    });

    useEffect(() => {
        if (open && country) {
            getDataDocuments(country.toUpperCase());
        }
    }, [open, country]);

    // Auto-cierre tras éxito — inlineado para evitar stale closure de isSubmitting en handleClose
    useEffect(() => {
        if (!showSuccess) return;
        const timer = setTimeout(() => {
            reset();
            setDocumentMask("");
            setDialCode("");
            setErrorMessage("");
            setShowSuccess(false);
            setShowExistingConfirm(false);
            setPendingLid("");
            onClose();
            onSuccess();
        }, 4000);
        return () => clearTimeout(timer);
    }, [showSuccess]);

    const handleClose = () => {
        if (isSubmitting) return;
        reset();
        setDocumentMask("");
        setDialCode("");
        setErrorMessage("");
        setShowSuccess(false);
        setShowExistingConfirm(false);
        setPendingLid("");
        onClose();
    };

    const handleDocumentTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const selected: any = documents.find((doc: any) => doc.code === event.target.value);
        if (selected) setDocumentMask(selected.input_mask ?? "");
    };

    // Segundo endpoint: crear pre-reserva
    const callCreatePreBooking = async (lid: string) => {
        setIsSubmitting(true);
        setErrorMessage("");
        try {
            const { status, data } = await callEndpoint(
                createPreBooking({ unit_id: unitId, landlord_id: lid }, token)
            );
            if (status === 200 || status === 201) {
                setShowExistingConfirm(false);
                setShowSuccess(true);
                return;
            }
            setErrorMessage(data?.message || "Error al crear la pre-reserva");
        } catch {
            setErrorMessage("Ha ocurrido un error al crear la pre-reserva");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Primer endpoint: crear cliente
    const handleFormSubmit = async (formData: CreateBookingFormData) => {
        const payload = { ...formData, username: formData.email, area_code: dialCode };
        console.log("CreateBooking payload:", payload);

        setIsSubmitting(true);
        setErrorMessage("");
        try {
            const { status, data } = await callEndpoint(
                createDeveloperCustomer(payload, token)
            );
            if (status !== 200 && status !== 201) {
                setErrorMessage(data?.message || "Error al crear el cliente");
                return;
            }
            const { lid, created } = data;

            if (created) {
                // Usuario nuevo → ir directo al segundo endpoint
                await callCreatePreBooking(lid);
            } else {
                // Usuario ya existía → pedir confirmación
                setPendingLid(lid);
                setShowExistingConfirm(true);
            }
        } catch {
            setErrorMessage("Ha ocurrido un error al procesar la solicitud");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Modal principal */}
            <Dialog
                open={open}
                onClose={handleClose}
                TransitionComponent={Transition}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <span className="text-xl lg:text-2xl font-bold tracking-tight text-[#212121]">
                        Crear una pre-reserva
                    </span>
                    {unitName && (
                        <span className="block text-sm font-normal text-[#757575]">
                            {unitName}
                        </span>
                    )}
                </DialogTitle>

                {showSuccess ? (
                    <DialogContent>
                        <div className="flex flex-col items-center justify-center py-10 text-center gap-4">
                            <IconCircleCheck className="text-primary size-16" />
                            <h3 className="text-lg lg:text-xl font-semibold text-[#212121] tracking-tight">
                                ¡Pre-reserva creada exitosamente!
                            </h3>
                            <p className="text-sm text-[#757575]">
                                Este modal se cerrará automáticamente en unos segundos.
                            </p>
                        </div>
                    </DialogContent>
                ) : (
                    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
                        <DialogContent className="space-y-4">
                            {errorMessage && (
                                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                                    <p className="text-sm text-red-600">{errorMessage}</p>
                                </div>
                            )}

                            {/* Nombre y Apellido */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <div className="w-full text-sm text-gray-700 font-medium mb-1">
                                        Nombre
                                    </div>
                                    <input
                                        type="text"
                                        className="bg-white block py-2 px-3 w-full text-sm rounded-md text-gray-900 border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                        {...register("first_name")}
                                    />
                                    {errors.first_name && (
                                        <p className="text-redValidation text-sm font-normal mt-1">
                                            {errors.first_name.message}
                                        </p>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="w-full text-sm text-gray-700 font-medium mb-1">
                                        Apellido
                                    </div>
                                    <input
                                        type="text"
                                        className="bg-white block py-2 px-3 w-full text-sm rounded-md text-gray-900 border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                        {...register("last_name")}
                                    />
                                    {errors.last_name && (
                                        <p className="text-redValidation text-sm font-normal mt-1">
                                            {errors.last_name.message}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {/* Correo electrónico */}
                            <div>
                                <div className="w-full text-sm text-gray-700 font-medium mb-1">
                                    Correo electrónico
                                </div>
                                <input
                                    type="email"
                                    className="bg-white block py-2 px-3 w-full text-sm rounded-md text-gray-900 border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                    {...register("email")}
                                />
                                {errors.email && (
                                    <p className="text-redValidation text-sm font-normal mt-1">
                                        {errors.email.message}
                                    </p>
                                )}
                            </div>
                            {/* Tipo y número de documento */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <div className="w-full text-sm text-gray-700 font-medium mb-1">
                                        Tipo de documento
                                    </div>
                                    {!loading && (
                                        <Controller
                                            name="document_type"
                                            control={control}
                                            render={({ field: { onChange, value } }) => (
                                                <Select
                                                    value={value}
                                                    onChange={(event: any) => {
                                                        onChange(event);
                                                        handleDocumentTypeChange(event);
                                                    }}
                                                    sx={{
                                                        width: "100%",
                                                        height: "38px",
                                                        borderColor: "#D1D5DB",
                                                        background: "white",
                                                        fontSize: "14px",
                                                    }}
                                                >
                                                    {documents.length > 0 &&
                                                        documents.map((item: any) => (
                                                            <MenuItem key={item.code} value={item.code}>
                                                                {item.document}
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                            )}
                                        />
                                    )}
                                    <FormControlError
                                        message={errors.document_type?.message}
                                        additionalClass="mb-0"
                                    />
                                </div>
                                <div className="flex-1">
                                    <div className="w-full text-sm text-gray-700 font-medium mb-1">
                                        Número de documento
                                    </div>
                                    <Controller
                                        control={control}
                                        name="document_number"
                                        render={({ field: { ref } }) => (
                                            <MaskInput
                                                ref={ref}
                                                register={register}
                                                name="document_number"
                                                mask={documentMask}
                                                customClass="bg-white w-full focus:outline-none border border-gray-300 rounded-md text-gray-700 py-2 px-2 text-sm"
                                            />
                                        )}
                                    />
                                    {errors.document_number && (
                                        <p className="text-redValidation text-sm font-normal mt-1">
                                            {errors.document_number.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Teléfono */}
                            <div>
                                <div className="w-full text-sm text-gray-700 font-medium mb-1">
                                    Número de teléfono
                                </div>
                                <PhoneSelector
                                    name="phone"
                                    control={control}
                                    onCountryCodeChange={(code) => setDialCode(code)}
                                    className="w-full"
                                />
                                {errors.phone && (
                                    <p className="text-redValidation text-sm font-normal mt-1">
                                        {errors.phone.message}
                                    </p>
                                )}
                            </div>

                            {/* Campo oculto */}
                            <input type="hidden" {...register("username")} />
                        </DialogContent>

                        <DialogActions className="px-6 pb-5 gap-2">
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" loading={isSubmitting}>
                                Crear pre-reserva
                            </Button>
                        </DialogActions>
                    </form>
                )}
            </Dialog>

            {/* Diálogo de confirmación: usuario ya existente */}
            <Dialog
                open={showExistingConfirm}
                onClose={() => { if (!isSubmitting) setShowExistingConfirm(false); }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <span className="text-lg font-semibold text-[#212121]">
                        Usuario ya registrado
                    </span>
                </DialogTitle>
                <DialogContent>
                    {errorMessage && (
                        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-3">
                            <p className="text-sm text-red-600">{errorMessage}</p>
                        </div>
                    )}
                    <p className="text-sm text-[#757575]">
                        Este usuario ya tiene una cuenta registrada en Propi.
                        ¿Deseas generarle una pre-reserva de todas formas?
                    </p>
                </DialogContent>
                <DialogActions className="px-6 pb-5 gap-2">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={() => setShowExistingConfirm(false)}
                        disabled={isSubmitting}
                    >
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        loading={isSubmitting}
                        onClick={() => callCreatePreBooking(pendingLid)}
                    >
                        Generar pre-reserva
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CreateBookingModal;
